import { cn } from "@/lib/utils";

/**
 * Tamaños del DS. El número es el diámetro y arrastra su propio cuerpo de
 * texto: agrandar el círculo sin agrandar las letras deja un avatar con dos
 * iniciales flotando en el medio.
 *
 *   fila     27px / 11px    — listados densos, bandeja, topbar
 *   listado  30px / 11.5px  — filas de trabajadores y papelera
 *   ficha    46px / 16px    — encabezado de identidad de una ficha
 */
export type TamanoAvatar = "fila" | "listado" | "ficha";

/**
 * Tono del círculo. Son los dos únicos del DS:
 *
 *   teal    — el caso normal, un registro vivo
 *   neutro  — gris: papelera, inactivos, y cualquier avatar que acompañe a un
 *             dato y no deba competir con él (la leyenda de un gráfico)
 *
 * No hay un tercero. Si un avatar necesita otro color, lo que cambió es el
 * ESTADO del registro, y eso lo dice el badge de al lado, no el avatar.
 */
export type TonoAvatar = "teal" | "neutro";

const TAMANOS: Record<TamanoAvatar, string> = {
  fila: "size-[27px] text-[11px]",
  listado: "size-[30px] text-[11.5px]",
  ficha: "size-[46px] text-base",
};

const TONOS: Record<TonoAvatar, string> = {
  teal: "bg-primary-100 text-primary-hover",
  neutro: "bg-muted text-soft",
};

export interface AvatarInicialesProps {
  /**
   * Las iniciales YA calculadas — dos letras. La mayoría de los recursos de la
   * API las traen en su propio campo `iniciales`; cuando sólo hay un nombre
   * armado, pasalas por `inicialesPersona()` (`@/lib/iniciales`) o
   * `inicialesEmpresa()` (`@/lib/empresa-marca`) en vez de recortarlas acá.
   */
  iniciales: string;
  tamano?: TamanoAvatar;
  tono?: TonoAvatar;
  /**
   * Nombre completo para el lector de pantalla. Sin esto el avatar se anuncia
   * deletreando dos letras sueltas ("J P"), que no le dice nada a nadie. Se
   * omite a propósito cuando el nombre ya está escrito al lado en la misma
   * fila: repetirlo haría que el lector lea a la persona dos veces.
   */
  nombre?: string;
  className?: string;
}

/**
 * Avatar de iniciales del Design System (§4bis.5).
 *
 * Círculo, DOS iniciales, peso 600. Nunca fotos: la app no las pide en ningún
 * alta y un avatar mitad foto mitad letra se ve roto. Nunca una sola letra
 * tampoco — ver el contrato de `inicialesPersona()`.
 *
 * Existía copiado en cinco pantallas (bandeja, ficha de trabajador, listado de
 * trabajadores, papelera, topbar del onboarding) con tres tamaños y dos tonos
 * escritos a mano cada vez. Las medidas ya habían empezado a correrse —28px
 * donde el DS dice 27— que es exactamente lo que pasa cuando el mismo círculo
 * se reescribe en cada lugar donde hace falta.
 */
export function AvatarIniciales({
  iniciales,
  tamano = "fila",
  tono = "teal",
  nombre,
  className,
}: AvatarInicialesProps) {
  return (
    <span
      // `flex-none` y no `shrink-0`: el avatar tampoco debe CRECER cuando es
      // hijo de un flex con espacio de sobra, o el círculo sale ovalado.
      className={cn(
        "flex flex-none items-center justify-center rounded-full font-semibold",
        TAMANOS[tamano],
        TONOS[tono],
        className,
      )}
      // El círculo es decorativo cuando el nombre ya está al lado; con `nombre`
      // pasa a ser una imagen con texto alternativo.
      aria-hidden={nombre ? undefined : true}
      role={nombre ? "img" : undefined}
      aria-label={nombre}
    >
      {iniciales}
    </span>
  );
}
